import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { MessageSquare } from "lucide-react";

// Wave A2 (2026-10) — the staff-side "missing half": agency comments (Reply/Resolve) + open
// item threads (Reply + the existing status dropdown, reused — not rebuilt). Self-contained:
// fetches its own agency-link check so it renders nothing on a project with no agency, and is
// cheap to drop into BOTH ProjectDashboard.jsx (gated there by hasScope) and
// AgencyWorkspaceManager.jsx (already agency-context, no extra gate needed there).
const STATUSES = [
  { v: "Yet-to-begin", label: "Yet to begin" }, { v: "In-progress", label: "In progress" }, { v: "Complete", label: "Complete" },
];
const when = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";

export default function AgencyThreadsPanel({ projectId }) {
  const [linked, setLinked] = useState(null);
  const [comments, setComments] = useState([]);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const reload = useCallback(async () => {
    try {
      const [c, i] = await Promise.all([
        api.get(`/v1/build/projects/${projectId}/agency-comments`),
        api.get(`/v1/build/projects/${projectId}/open-items`),
      ]);
      setComments(c.data.items || []);
      setItems(i.data.open_items || []);
    } catch { /* link check below decides visibility; a transient fetch error just leaves stale data */ }
  }, [projectId]);

  useEffect(() => {
    api.get(`/v1/build/projects/${projectId}/agency-link`).then((r) => {
      setLinked(r.data.linked);
      if (r.data.linked) {
        reload();
        // Part B (2026-10), item 1 — opening this panel (the project's Communication tab) IS
        // "reading" the agency thread: fetch the count BEFORE marking read so the badge still
        // shows what was unread a moment ago, then clear it for next time.
        api.get(`/v1/build/projects/${projectId}/agency-comments/unread-count`).then((u) => setUnreadCount(u.data.count || 0)).catch(() => {});
        api.post(`/v1/build/projects/${projectId}/agency-comments/mark-read`).catch(() => {});
      }
    }).catch(() => setLinked(false));
  }, [projectId, reload]);

  if (!linked) return null;
  const openItemCount = items.filter((it) => it.created_by_party === "partner" && it.status !== "Complete" && (!it.comments?.length || it.comments[it.comments.length - 1].author_type !== "njm")).length;
  const newCount = unreadCount + openItemCount;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="agency-threads-panel">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-base font-semibold">Agency Activity</h3>
        {newCount > 0 && <span className="text-xs font-medium rounded-full bg-amber-500/15 text-amber-600 px-2 py-0.5" data-testid="agency-threads-badge">{newCount} new from agency</span>}
      </div>
      <AgencyCommentsSection projectId={projectId} comments={comments} reload={reload} />
      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-sm font-medium text-muted-foreground mb-3">Open items</div>
        {items.length === 0 ? <div className="text-sm text-muted-foreground">No open items yet.</div> :
          items.map((it) => <OpenItemThread key={it.id} projectId={projectId} it={it} reload={reload} />)}
      </div>
    </div>
  );
}

function AgencyCommentsSection({ projectId, comments, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const reply = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/v1/build/projects/${projectId}/agency-comments`, { text: text.trim() }); setText(""); await reload(); } finally { setBusy(false); }
  };
  // Part B (2026-10), item 2 — Resolve/Reopen removed from this panel entirely (it never made
  // sense here: a project-level agency thread isn't a ticket queue to "close"). The backend
  // `/agency-comments/{id}/resolve` route itself is untouched — RecceCommentThread.jsx (a
  // Recce surface, out of scope for this pass) still uses it for its own per-location threads.
  return (
    <div data-testid="agency-threads-comments">
      <div className="text-sm font-medium text-muted-foreground mb-3">Comments</div>
      {comments.length === 0 ? <div className="text-sm text-muted-foreground mb-3">No comments yet.</div> : (
        <div className="space-y-2 mb-3">
          {comments.map((c) => (
            <div key={c.id} className="text-sm" data-testid="agency-thread-comment-row">
              <strong>{c.author_type === "partner" ? "Agency" : c.author_name || "NJM"}:</strong> {c.text}
              <span className="text-muted-foreground"> · {when(c.at)}</span>
            </div>
          ))}
        </div>
      )}
      <textarea className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" rows={2} value={text}
        onChange={(e) => setText(e.target.value)} placeholder="Reply to the agency…" data-testid="agency-threads-comment-input" />
      <button className="mt-2 text-sm font-medium rounded-md bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50" onClick={reply} disabled={busy} data-testid="agency-threads-comment-reply">{busy ? "Sending…" : "Reply"}</button>
    </div>
  );
}

function OpenItemThread({ projectId, it, reload }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const setStatus = async (status) => { await api.post(`/v1/build/projects/${projectId}/open-items`, { id: it.id, status }); await reload(); };
  const reply = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/v1/build/projects/${projectId}/open-items/${it.id}/comments`, { text: text.trim() }); setText(""); await reload(); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-lg p-3 mb-2" data-testid="agency-threads-item">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">{it.title}{it.created_by_party === "partner" && <span className="ml-2 text-xs rounded-full bg-muted px-2 py-0.5 text-muted-foreground">Started by agency</span>}</div>
        <select className="text-xs rounded-md border border-input bg-background px-2 py-1" value={it.status} onChange={(e) => setStatus(e.target.value)} data-testid="agency-threads-item-status">
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>
      {it.details && <div className="text-xs text-muted-foreground mt-1">{it.details}</div>}
      {/* Part B (2026-10), item 3 — read-only here (full reassign picker lives on
          AgencyWorkspaceManager.jsx, the management surface); still worth showing at a glance. */}
      <div className="text-xs text-muted-foreground mt-1" data-testid="agency-threads-item-assignee">Assigned to: {it.assignee_name || "Unassigned"}</div>
      {it.comments?.length > 0 && (
        <div className="mt-2 space-y-1">
          {it.comments.map((c, i) => <div key={i} className="text-xs"><strong>{c.author_type === "partner" ? "Agency" : "You"}:</strong> {c.text} <span className="text-muted-foreground">· {when(c.at)}</span></div>)}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <input className="flex-1 text-xs rounded-md border border-input bg-background px-2 py-1" value={text} onChange={(e) => setText(e.target.value)} placeholder="Reply…" data-testid="agency-threads-item-reply-input" />
        <button className="text-xs font-medium rounded-md bg-primary text-primary-foreground px-2 py-1 disabled:opacity-50" onClick={reply} disabled={busy} data-testid="agency-threads-item-reply-submit">Send</button>
      </div>
    </div>
  );
}
